/**
 * Workspace Store - Central state management for Product Manager IDE
 * Manages modes, journey, detail panes, and workspace context
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type WorkspaceMode = 'dashboard' | 'full-ai' | 'canvas' | 'smart-doc' | 'entities' | 'flow'
export type JourneyMode = 'structured' | 'free'

interface DetailPaneState {
  uploadDocs: boolean
  aiCoach: boolean
  healthScore: boolean
  journey: boolean
}

interface WorkspaceContext {
  mode: WorkspaceMode
  phaseId: string
  journeyMode: JourneyMode
  sessionId?: string
  canvasInstanceUri?: string
  documentId?: string
}

interface HealthMetrics {
  overall: number
  phaseScores: Record<string, number>
  lastUpdated: string
}

interface WorkspaceState {
  // Mode state
  activeMode: WorkspaceMode
  setActiveMode: (mode: WorkspaceMode) => void

  // Journey state
  currentPhaseId: string
  setCurrentPhaseId: (phaseId: string) => void
  journeyMode: JourneyMode
  setJourneyMode: (mode: JourneyMode) => void

  // Phase completion tracking (for structured mode locking)
  completedPhases: Set<string>
  markPhaseComplete: (phaseId: string) => void
  markPhaseIncomplete: (phaseId: string) => void
  isPhaseAccessible: (phaseId: string, phases: Array<{ id: string; order: number }>) => boolean

  // Detail pane visibility
  detailPanes: DetailPaneState
  togglePane: (pane: keyof DetailPaneState) => void
  expandPane: (pane: keyof DetailPaneState) => void
  collapsePane: (pane: keyof DetailPaneState) => void

  // Context state (passed to child components)
  workspaceContext: WorkspaceContext
  updateContext: (partial: Partial<WorkspaceContext>) => void

  // Health metrics (updated by child components)
  healthMetrics: HealthMetrics
  updateHealthMetrics: (metrics: Partial<HealthMetrics>) => void

  // Reset to defaults
  reset: () => void
}

// Default state
const defaultState = {
  activeMode: 'dashboard' as WorkspaceMode,
  currentPhaseId: '',
  journeyMode: 'structured' as JourneyMode,
  completedPhases: new Set<string>(),
  detailPanes: {
    uploadDocs: true,
    aiCoach: true,
    healthScore: true,
    journey: true,
  },
  workspaceContext: {
    mode: 'dashboard' as WorkspaceMode,
    phaseId: '',
    journeyMode: 'structured' as JourneyMode,
  },
  healthMetrics: {
    overall: 0,
    phaseScores: {},
    lastUpdated: new Date().toISOString(),
  },
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      ...defaultState,

      // Mode management
      setActiveMode: (mode) =>
        set((state) => ({
          activeMode: mode,
          workspaceContext: {
            ...state.workspaceContext,
            mode,
          },
        })),

      // Journey management
      setCurrentPhaseId: (phaseId) =>
        set((state) => ({
          currentPhaseId: phaseId,
          workspaceContext: {
            ...state.workspaceContext,
            phaseId,
          },
        })),

      setJourneyMode: (mode) =>
        set((state) => ({
          journeyMode: mode,
          workspaceContext: {
            ...state.workspaceContext,
            journeyMode: mode,
          },
        })),

      // Phase completion
      markPhaseComplete: (phaseId) =>
        set((state) => {
          const newCompleted = new Set(state.completedPhases)
          newCompleted.add(phaseId)
          return { completedPhases: newCompleted }
        }),

      markPhaseIncomplete: (phaseId) =>
        set((state) => {
          const newCompleted = new Set(state.completedPhases)
          newCompleted.delete(phaseId)
          return { completedPhases: newCompleted }
        }),

      isPhaseAccessible: (phaseId, phases) => {
        const state = get()

        // Free mode: all phases accessible
        if (state.journeyMode === 'free') {
          return true
        }

        // Structured mode: check if previous phases are complete
        const phase = phases.find((p) => p.id === phaseId)
        if (!phase) return false

        // First phase is always accessible
        if (phase.order === 0) return true

        // Check if all previous phases are completed
        const previousPhases = phases.filter((p) => p.order < phase.order)
        return previousPhases.every((p) => state.completedPhases.has(p.id))
      },

      // Detail pane management
      togglePane: (pane) =>
        set((state) => ({
          detailPanes: {
            ...state.detailPanes,
            [pane]: !state.detailPanes[pane],
          },
        })),

      expandPane: (pane) =>
        set((state) => ({
          detailPanes: {
            ...state.detailPanes,
            [pane]: true,
          },
        })),

      collapsePane: (pane) =>
        set((state) => ({
          detailPanes: {
            ...state.detailPanes,
            [pane]: false,
          },
        })),

      // Context management
      updateContext: (partial) =>
        set((state) => ({
          workspaceContext: {
            ...state.workspaceContext,
            ...partial,
          },
        })),

      // Health metrics
      updateHealthMetrics: (metrics) =>
        set((state) => ({
          healthMetrics: {
            ...state.healthMetrics,
            ...metrics,
            lastUpdated: new Date().toISOString(),
          },
        })),

      // Reset
      reset: () =>
        set({
          ...defaultState,
          completedPhases: new Set(),
        }),
    }),
    {
      name: 'workspace-storage',
      // Custom serialization for Set
      partialize: (state) => ({
        activeMode: state.activeMode,
        currentPhaseId: state.currentPhaseId,
        journeyMode: state.journeyMode,
        completedPhases: Array.from(state.completedPhases),
        detailPanes: state.detailPanes,
        workspaceContext: state.workspaceContext,
        healthMetrics: state.healthMetrics,
      }),
      // Custom deserialization for Set
      onRehydrateStorage: () => (state) => {
        if (state && Array.isArray(state.completedPhases)) {
          state.completedPhases = new Set(state.completedPhases as unknown as string[])
        }
      },
    }
  )
)
